import React, { useEffect, useState } from "react";
import { Form, Input, Button, Space, notification } from "antd";
import { clinicalNotesService } from "../../services/clinicalNotesService";
import { ClinicalNotesDTO } from "../../models/ClinicalNotes";

const { TextArea } = Input;

interface NotizieClinicheProps {
  caseId: number;
  isEditable: boolean;
  onSectionComplete: () => void;
}

const NotizieCliniche: React.FC<NotizieClinicheProps> = ({
  caseId,
  isEditable,
  onSectionComplete,
}) => {
  const [loadingFamiliare, setLoadingFamiliare] = useState(false);
  const [loadingPatologica, setLoadingPatologica] = useState(false);
  const [formFamiliare] = Form.useForm();
  const [formPatologica] = Form.useForm();

  const [familiareNote, setFamiliareNote] = useState<ClinicalNotesDTO | null>(
    null
  );
  const [patologicaNote, setPatologicaNote] = useState<ClinicalNotesDTO | null>(
    null
  );

  useEffect(() => {
    const loadData = async () => {
      try {
        const [familiareResponse, patologicaResponse] = await Promise.all([
          clinicalNotesService.getAllClinicalNotes({
            page: 0,
            "casesId.equals": caseId,
            "noteType.equals": "ANAMNESI_FAMILIARE",
          }),
          clinicalNotesService.getAllClinicalNotes({
            page: 0,
            "casesId.equals": caseId,
            "noteType.equals": "ANAMNESI_PATOLOGICA",
          }),
        ]);

        const familiare =
          familiareResponse.length > 0 ? familiareResponse[0] : null;
        const patologica =
          patologicaResponse.length > 0 ? patologicaResponse[0] : null;

        setFamiliareNote(familiare);
        setPatologicaNote(patologica);

        formFamiliare.setFieldsValue({
          description: familiare ? familiare.description : "",
        });
        formPatologica.setFieldsValue({
          description: patologica ? patologica.description : "",
        });
      } catch (error) {
        console.error("Errore durante il caricamento dei dati:", error);
      }
    };
    loadData();
  }, [caseId, formFamiliare, formPatologica]);

  const handleSave = async (
    type: "ANAMNESI_FAMILIARE" | "ANAMNESI_PATOLOGICA",
    form: any,
    setLoading: (loading: boolean) => void
  ) => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const noteData: ClinicalNotesDTO = {
        noteType: type,
        description: values.description,
        cases: {
          id: caseId,
          createdAt: new Date().toISOString(),
        },
      };

      if (type === "ANAMNESI_FAMILIARE" && familiareNote && familiareNote.id) {
        await clinicalNotesService.updateClinicalNote(familiareNote.id, {
          ...noteData,
          id: familiareNote.id,
        });
        notification.success({
          message: "Anamnesi familiare aggiornata con successo",
        });
      } else if (
        type === "ANAMNESI_PATOLOGICA" &&
        patologicaNote &&
        patologicaNote.id
      ) {
        await clinicalNotesService.updateClinicalNote(patologicaNote.id, {
          ...noteData,
          id: patologicaNote.id,
        });
        notification.success({
          message: "Anamnesi patologica aggiornata con successo",
        });
      } else {
        await clinicalNotesService.createClinicalNote(noteData);
        notification.success({
          message: `Anamnesi ${
            type === "ANAMNESI_FAMILIARE" ? "familiare" : "patologica"
          } salvata con successo`,
        });
      }

      onSectionComplete();
    } catch (error) {
      console.error("Errore durante il salvataggio:", error);
      notification.error({
        message: "Si è verificato un errore durante il salvataggio",
      });
    } finally {
      setLoading(false);
    }
  };

  const renderTextArea = (name: string, label: string, form: any) => {
    return (
      <Form.Item
        name={name}
        label={label}
        rules={
          isEditable
            ? [
                {
                  required: true,
                  message: `Inserisci l'${label.toLowerCase()}`,
                },
              ]
            : undefined
        }
      >
        {isEditable ? (
          <TextArea rows={4} />
        ) : (
          <Input.TextArea
            readOnly
            value={form.getFieldValue(name) || ""}
            className="read-only-input"
            rows={4}
          />
        )}
      </Form.Item>
    );
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Form form={formFamiliare} layout="vertical">
        {renderTextArea("description", "Anamnesi Familiare", formFamiliare)}
        {isEditable && (
          <Form.Item>
            <Button
              type="primary"
              onClick={() =>
                handleSave(
                  "ANAMNESI_FAMILIARE",
                  formFamiliare,
                  setLoadingFamiliare
                )
              }
              loading={loadingFamiliare}
            >
              {familiareNote
                ? "Aggiorna Anamnesi Familiare"
                : "Salva Anamnesi Familiare"}
            </Button>
          </Form.Item>
        )}
      </Form>

      <Form form={formPatologica} layout="vertical">
        {renderTextArea("description", "Anamnesi Patologica", formPatologica)}
        {isEditable && (
          <Form.Item>
            <Button
              type="primary"
              onClick={() =>
                handleSave(
                  "ANAMNESI_PATOLOGICA",
                  formPatologica,
                  setLoadingPatologica
                )
              }
              loading={loadingPatologica}
            >
              {patologicaNote
                ? "Aggiorna Anamnesi Patologica"
                : "Salva Anamnesi Patologica"}
            </Button>
          </Form.Item>
        )}
      </Form>
    </Space>
  );
};

export default NotizieCliniche;
